<template>
  <!-- Campaign / group activity card -->
  <f7-card
    v-if="card.kind === 'campaign'"
    class="feed-card bg-surface-pure shadow-avocash-sm"
    :padding="false"
    role="button"
    tabindex="0"
    @click="continueToCampaign"
    @keydown.enter="continueToCampaign"
    @keydown.space.prevent="continueToCampaign"
  >
    <f7-card-content class="feed-card-content" :padding="false">
      <div class="fc-header display-flex align-items-center gap-2 mb-1.5">
        <div class="fc-avatar display-flex align-items-center justify-content-center flex-shrink-0" :class="avatarColorClass(card.authorColor)" style="width: 32px; height: 32px; border-radius: 50%; font-size: 10px; font-weight: 500;">
          {{ card.authorInitials }}
        </div>
        <div class="fc-meta flex-1 min-w-0">
          <div class="fc-author font-label-sm font-medium text-on-surface">{{ card.authorName }}</div>
          <div class="fc-sub font-label-xs text-on-surface-variant">{{ campaignLabel }}</div>
        </div>
        <f7-badge :class="tagColorClass(card.tagColor)" pill>{{ card.tag }}</f7-badge>
      </div>
      <div class="fc-title font-label-md font-semibold text-on-surface mb-1">{{ card.title }}</div>
      <div v-if="card.metaLine" class="fc-meta-line font-label-xs text-on-surface-variant mb-1">{{ card.metaLine }}</div>
      <div class="fc-desc font-body-sm text-on-surface-variant leading-relaxed" v-html="card.description"></div>

      <div v-if="card.progress" class="fc-progress bg-surface-container-low rounded-lg p-2 border border-surface-variant mt-2">
        <div class="fc-progress-header display-flex justify-content-space-between font-label-xs text-on-surface-variant mb-1">
          <span>{{ card.progress.label }}</span>
          <span v-if="card.extra">{{ card.extra }}</span>
        </div>
        <f7-progressbar class="fc-progress-bar" :progress="progressPct" :style="{ '--f7-progressbar-height': '6px', '--f7-progressbar-bg-color': '#E5E7EB' }" />
        <div v-if="card.raised != null && card.goal != null" class="fc-fund-meta display-flex justify-content-space-between gap-2 font-label-xs text-on-surface-variant mt-1.5">
          <span>{{ formatAmount(card.raised) }} {{ card.currency }} · {{ formatAmount(card.goal) }} {{ card.currency }}</span>
          <span v-if="card.contributors || card.deadlineLabel">
            {{ card.contributors }} {{ $t('feedCard.contributors') }}<span v-if="card.deadlineLabel"> · {{ $t('feedCard.deadline') }} {{ card.deadlineLabel }}</span>
          </span>
        </div>
      </div>

      <div v-if="card.route" class="fc-actions display-flex align-items-center gap-2 mt-2 pt-2 border-t border-surface-variant">
        <f7-link class="fc-action-link font-label-sm font-medium" :class="actionLinkColorClass(card.tagColor)" @click.stop="continueToCampaign">
          {{ keycloakStore.authenticated ? $t('common.seeMore') : $t('feedCard.joinToContribute') }}
        </f7-link>
      </div>
    </f7-card-content>
  </f7-card>

  <!-- Sponsored card -->
  <f7-card
    v-else-if="card.kind === 'sponsored'"
    class="feed-card feed-card--sponsored bg-gold-light shadow-avocash-sm"
    :padding="false"
  >
    <f7-card-content class="feed-card-content" :padding="false">
      <f7-link class="fc-sponsored-label display-inline-flex align-items-center gap-1 px-2 py-1 rounded bg-gold-light text-warning font-label-xs font-medium mb-2" icon-f7="speaker_2_fill" icon-size="10" :href="card.sponsorUrl">
        {{ card.sponsorLabel || $t('feedCard.sponsored') }}
      </f7-link>
      <div class="fc-title font-label-md font-semibold text-on-surface mb-1">{{ card.title }}</div>
      <div class="fc-desc font-body-sm text-on-surface-variant leading-relaxed" v-html="card.description"></div>
      <div class="fc-actions display-flex align-items-center gap-2 mt-2 pt-2 border-t border-surface-variant">
        <f7-link class="fc-action-link font-label-sm font-medium text-warning" @click.stop="continueToCampaign">{{ $t('common.knowMore') }}</f7-link>
      </div>
      <f7-button class="fc-why fill small" @click.stop="showWhyThisAd" style="margin-top: 8px;">{{ $t('feedCard.whyThisAd') }}</f7-button>
    </f7-card-content>
  </f7-card>

  <!-- Poll card -->
  <f7-card
    v-else-if="card.kind === 'poll'"
    class="feed-card bg-surface-pure shadow-avocash-sm"
    :padding="false"
  >
    <f7-card-content class="feed-card-content" :padding="false">
      <div class="fc-header display-flex align-items-center gap-2 mb-1.5">
        <div class="fc-avatar display-flex align-items-center justify-content-center flex-shrink-0 bg-info text-on-info" style="width: 32px; height: 32px; border-radius: 50%; font-size: 10px; font-weight: 500;">
          {{ card.authorInitials }}
        </div>
        <div class="fc-meta flex-1 min-w-0">
          <div class="fc-author font-label-sm font-medium text-on-surface">{{ card.authorName }}</div>
          <div class="fc-sub font-label-xs text-on-surface-variant">{{ $t('common.poll') }} · {{ card.totalVotes }} {{ $t('common.vote') }}</div>
        </div>
      </div>
      <div class="fc-title font-label-md font-semibold text-on-surface mb-2">{{ card.title }}</div>
      <div class="fc-poll-options display-flex flex-direction-column gap-1.5">
        <f7-card
          v-for="opt in card.options"
          :key="opt.id"
          class="fc-poll-option bg-surface-container rounded-lg overflow-hidden"
          :padding="false"
        >
          <f7-card-content class="fc-poll-option-content" :padding="false">
            <f7-progressbar
              class="fc-poll-bar absolute inset-0"
              :progress="Math.round((opt.votes / card.totalVotes) * 100)"
              :style="{ '--f7-progressbar-height': '100%', '--f7-progressbar-bg-color': 'transparent', '--f7-progressbar-color': 'var(--avocash-blue-light, #EBF0FC)', 'border-radius': '6px' }"
            />
            <div class="fc-poll-info relative display-flex justify-content-space-between font-label-sm text-on-surface p-2">
              <span>{{ opt.label }}</span>
              <span class="font-medium">{{ Math.round((opt.votes / card.totalVotes) * 100) }}%</span>
            </div>
          </f7-card-content>
        </f7-card>
      </div>
    </f7-card-content>
  </f7-card>

  <!-- Activity card (P2P transfer, contribution) -->
  <f7-card
    v-else-if="card.kind === 'activity'"
    class="feed-card feed-card--activity bg-surface-pure shadow-avocash-sm"
    :padding="false"
  >
    <f7-card-content class="feed-card-content" :padding="false">
      <div class="fc-header display-flex align-items-center gap-2 mb-1.5">
        <div class="fc-avatar display-flex align-items-center justify-content-center flex-shrink-0" :class="activityAvatarColorClass(card.activityType)" style="width: 32px; height: 32px; border-radius: 50%; font-size: 10px; font-weight: 500;">
          {{ card.userInitials }}
        </div>
        <div class="fc-meta flex-1 min-w-0">
          <div class="fc-author font-label-sm font-medium text-on-surface">{{ card.userName }}</div>
          <div class="fc-sub font-label-xs text-on-surface-variant">{{ card.detail }} · {{ card.timestamp }}</div>
        </div>
      </div>
      <div class="fc-desc fc-desc--activity font-body-sm text-on-surface leading-relaxed pl-9" v-html="card.description"></div>
    </f7-card-content>
  </f7-card>
</template>

<script setup>
import { computed } from 'vue'
import { f7 } from 'framework7-vue'
import i18n from '../../js/i18n.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'

const { t } = i18n.global

const props = defineProps({
  card: { type: Object, required: true },
})

const keycloakStore = useKeycloakStore()

function formatAmount(value) {
  if (value == null || isNaN(value)) return value
  return new Intl.NumberFormat(i18n.global.locale.value, { maximumFractionDigits: 0 }).format(value)
}

function showWhyThisAd() {
  f7.dialog.alert(t('feedCard.whyThisAdBody'), t('feedCard.whyThisAd'))
}

function continueToCampaign() {
  if (!keycloakStore.authenticated) {
    keycloakStore.keycloak.register()
    return
  }
  if (props.card.route) f7.views.main.router.navigate(props.card.route)
}

const campaignTypeLabels = computed(() => ({
  TONTINE: t('feedCard.TONTINE'),
  FUND_COLLECT: t('feedCard.FUND_COLLECT'),
  GROUP_BUY: t('feedCard.GROUP_BUY'),
  GROUP_SAVINGS: t('feedCard.GROUP_SAVINGS'),
  TICKETING: t('feedCard.TICKETING'),
  DONATION_DRIVE: t('feedCard.DONATION_DRIVE'),
  EVENT_REGISTRATION: t('feedCard.EVENT_REGISTRATION'),
  AUCTION: t('feedCard.AUCTION'),
  MEMBERSHIP: t('feedCard.MEMBERSHIP'),
}))

const campaignLabel = computed(() =>
  campaignTypeLabels.value[props.card.campaignType] || ''
)

const progressPct = computed(() => {
  if (!props.card.progress) return 0
  return Math.min(100, Math.round((props.card.progress.current / props.card.progress.total) * 100))
})

function avatarColorClass(color) {
  const map = {
    green: 'bg-primary-fixed text-primary',
    blue: 'bg-info-fixed text-info',
    purple: 'bg-purple-fixed text-purple',
    gold: 'bg-warning-fixed text-warning',
    leaf: 'bg-success-fixed text-success',
  }
  return map[color] || 'bg-primary-fixed text-primary'
}

function tagColorClass(color) {
  const map = {
    green: 'bg-primary-fixed text-on-primary-fixed',
    blue: 'bg-info-fixed text-on-info-fixed',
    purple: 'bg-purple-fixed text-on-purple-fixed',
    gold: 'bg-warning-fixed text-on-warning-fixed',
    leaf: 'bg-success-fixed text-on-success-fixed',
  }
  return map[color] || 'bg-primary-fixed text-on-primary-fixed'
}

function actionLinkColorClass(color) {
  const map = {
    green: 'text-primary',
    blue: 'text-info',
    purple: 'text-purple',
    gold: 'text-warning',
    leaf: 'text-success',
  }
  return map[color] || 'text-primary'
}

function activityAvatarColorClass(type) {
  return type === 'received' ? 'bg-primary-fixed text-primary' : 'bg-info-fixed text-info'
}
</script>

<style scoped>
.feed-card {
  margin-bottom: 8px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 10px;
}
.feed-card--sponsored {
  border-color: var(--avocash-gold, #C9A84C);
}
.feed-card--activity {
  padding: 8px 10px;
}
.feed-card-content {
  padding: 10px;
}
.fc-header {
  margin-bottom: 6px;
}
.fc-author {
  font-size: 11px;
  font-weight: 500;
}
.fc-sub {
  font-size: 9px;
  margin-top: 1px;
}
.fc-title {
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 2px;
}
.fc-desc {
  font-size: 11px;
  line-height: 1.45;
}
.fc-desc--activity {
  font-size: 11px;
  padding-left: 39px;
  margin-top: -2px;
}
.fc-sponsored-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 500;
  border-radius: 6px;
  padding: 2px 6px;
  margin-bottom: 5px;
}
.fc-progress {
  margin-top: 6px;
  border-radius: 7px;
}
.fc-progress-header {
  margin-bottom: 3px;
}
.fc-progress-bar {
  --f7-progressbar-height: 6px;
}
.fc-fund-meta {
  gap: 8px;
  margin-top: 4px;
}
.fc-why {
  margin-top: 4px;
}
.fc-poll-options {
  margin-top: 6px;
  gap: 4px;
}
.fc-poll-option {
  position: relative;
  border-radius: 6px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  overflow: hidden;
}
.fc-poll-option-content {
  padding: 6px 8px;
  position: relative;
}
.fc-poll-bar {
  --f7-progressbar-height: 100%;
}
.fc-poll-info {
  position: relative;
  font-size: 10px;
}
.fc-actions {
  margin-top: 6px;
}
.fc-action-link {
  font-size: 10px;
  font-weight: 500;
}
</style>
